/**
 * The course builder's pure helpers (docs/plans/lms-course-builder-plan.md
 * 4.9, 6.1, 6.2, 6.4, 6.12, 7.3, 8.1, 8.2, 9.2, 18.2 to 18.5; WP-B30): the
 * builder's paths inside the catalog sub-view, the stage labels and the stage
 * list of a run, the status pill words, the progress line, the size targets a
 * category offers, the source minimum, the checks the wizard makes before it
 * sends anything (the server re-checks every one), and the plain words for
 * every error code a builder route or a run can carry. No React, no I/O.
 *
 * Vocabulary (plan 2.3): run, source, stage, estimate, cap. The bare word for
 * an AI spend ceiling is never used as an identifier here (LMS naming rule).
 */
import type { CourseCategory, CourseKind } from '../../../../content/types';
import {
  BUILDER_FILE_MAX_BYTES,
  BUILDER_LINK_MAX_CHARS,
  BUILDER_POOL_SIZES,
  BUILDER_SCENARIO_POOL_SIZES,
  BUILDER_SCENARIO_TARGET_MINUTES,
  BUILDER_TARGET_MINUTES,
  centsOf,
} from '../../../../api/builder';
import type { BuilderConfig, BuilderEstimate, BuilderRun, BuilderRunMode, BuilderRunStatus, BuilderStage } from '../../../../api/builder';
import { CATALOG_BASE, CATEGORY_LABELS } from '../model';

// ---------------------------------------------------------------------------
// Paths (the catalog sub-view is mounted at /lms/manage/catalog/*)
// ---------------------------------------------------------------------------

export const builderPaths = {
  /** 8.1: the wizard; with a course id it opens in extend mode (?course=<id>). */
  build: (courseId?: string | null) =>
    courseId ? `${CATALOG_BASE}/build?course=${encodeURIComponent(courseId)}` : `${CATALOG_BASE}/build`,
  /** 8.2 step 4: one run's progress and outcome. */
  run: (runId: string) => `${CATALOG_BASE}/runs/${encodeURIComponent(runId)}`,
  /** 7.3: the platform link a built course's sources list carries for an uploaded file. */
  source: (sourceId: string) => `${CATALOG_BASE}/sources/${encodeURIComponent(sourceId)}`,
};

// ---------------------------------------------------------------------------
// Statuses and stages (5.1, 6.1, 18.3)
// ---------------------------------------------------------------------------

/** The statuses a run holds while its second job builds (the partial unique of 5.1). */
export const BUILDING_STATUSES: readonly BuilderRunStatus[] = [
  'queued',
  'structuring',
  'authoring',
  'assembling',
  'repairing',
  'writing',
];

/** The closing section's title (LMS plan 6.2 rule 10); an extend run revises it through its own box. */
export const CLOSING_SECTION_TITLE = 'Putting it into practice at the site';

/** True for the closing section of a course. */
export function isClosingSectionTitle(title: string | null | undefined): boolean {
  return (title || '').trim().toLowerCase() === CLOSING_SECTION_TITLE.toLowerCase();
}

/** True while the builder is writing (the "Building" pill of 8.1). */
export function isBuildingStatus(status: string | null | undefined): boolean {
  return (BUILDING_STATUSES as readonly string[]).includes(String(status));
}

/** True while the first job reads the files (before the estimate). */
export function isReadingStatus(status: string | null | undefined): boolean {
  return status === 'uploaded' || status === 'extracting';
}

export type PillTone = 'green' | 'amber' | 'red' | 'gray' | 'primary';

/** 8.6: a pill always carries its word. */
export function statusPill(status: BuilderRunStatus | string | null | undefined): { label: string; tone: PillTone } {
  switch (status) {
    case 'uploaded':
    case 'extracting':
      return { label: 'Reading files', tone: 'primary' };
    case 'estimated':
      return { label: 'Estimated', tone: 'amber' };
    case 'queued':
      return { label: 'Queued', tone: 'primary' };
    case 'structuring':
    case 'authoring':
    case 'assembling':
    case 'repairing':
    case 'writing':
      return { label: 'Building', tone: 'primary' };
    case 'done':
      return { label: 'Done', tone: 'green' };
    case 'failed':
      return { label: 'Failed', tone: 'red' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'gray' };
    default:
      return { label: 'Unknown', tone: 'gray' };
  }
}

export const STAGE_LABELS: Record<BuilderStage, string> = {
  upload: 'Upload the files',
  extract: 'Read the files',
  estimate: 'Estimate the cost',
  structure: 'Plan the course',
  author: 'Write the sections',
  assemble: 'Assemble the course',
  repair: 'Check and repair',
  write: 'Save the draft',
};

/** The stages a run of `mode` passes through, in order (6.1; a section run has no structure stage, 18.5). */
export function stagesFor(mode: BuilderRunMode): BuilderStage[] {
  if (mode === 'section') return ['author', 'assemble', 'repair', 'write'];
  return ['upload', 'extract', 'estimate', 'structure', 'author', 'assemble', 'repair', 'write'];
}

export type StageState = 'done' | 'current' | 'pending' | 'failed' | 'stopped';

export interface StageRow {
  stage: BuilderStage;
  label: string;
  state: StageState;
}

/** The stage the run is in: the row's stage, else what the status implies. */
function currentStageOf(run: Pick<BuilderRun, 'status' | 'stage' | 'mode'>): BuilderStage | null {
  const stages = stagesFor(run.mode);
  if (run.stage && (stages as string[]).includes(run.stage)) return run.stage as BuilderStage;
  switch (run.status) {
    case 'uploaded':
      return stages.includes('upload') ? 'upload' : null;
    case 'extracting':
      return 'extract';
    case 'estimated':
      return 'estimate';
    case 'structuring':
      return 'structure';
    case 'authoring':
      return 'author';
    case 'assembling':
      return 'assemble';
    case 'repairing':
      return 'repair';
    case 'writing':
      return 'write';
    default:
      return null;
  }
}

/** The stage list of 8.2 step 4: every stage done, current, pending, failed or stopped. */
export function stageRows(run: Pick<BuilderRun, 'status' | 'stage' | 'mode'>): StageRow[] {
  const stages = stagesFor(run.mode);
  const current = currentStageOf(run);
  const at = current ? stages.indexOf(current) : -1;
  return stages.map((stage, i) => {
    let state: StageState;
    if (run.status === 'done') state = 'done';
    else if (run.status === 'queued') state = at >= 0 && (i < at || (i === at && ['upload', 'extract', 'estimate'].includes(stage))) ? 'done' : 'pending';
    else if (at < 0) state = 'pending';
    else if (i < at) state = 'done';
    else if (i > at) state = 'pending';
    else if (run.status === 'failed') state = 'failed';
    else if (run.status === 'cancelled') state = 'stopped';
    else if (run.status === 'estimated') state = 'done';
    else state = 'current';
    return { stage, label: STAGE_LABELS[stage], state };
  });
}

/** "Section 3 of 9" while writing, or null. */
export function sectionLine(run: Pick<BuilderRun, 'progress'>): string | null {
  const p = run.progress || {};
  const total = typeof p.sections_total === 'number' ? p.sections_total : null;
  const done = typeof p.sections_done === 'number' ? p.sections_done : 0;
  if (!total || total < 1) return null;
  const n = Math.min(done + (done < total ? 1 : 0), total);
  return done >= total ? `All ${total} sections written` : `Section ${n} of ${total}`;
}

/** The share of sections written, 0 to 100 (the progress bar). */
export function sectionPercent(run: Pick<BuilderRun, 'progress'>): number {
  const p = run.progress || {};
  const total = typeof p.sections_total === 'number' ? p.sections_total : 0;
  const done = typeof p.sections_done === 'number' ? p.sections_done : 0;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
}

/** "12 min 5 s", "45 s", "1 h 3 min" (the run page's elapsed time). */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0 s';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min ${sec} s`;
  return `${sec} s`;
}

/** Milliseconds from the run's start (or creation) to its end (or `now`). */
export function elapsedMs(run: Pick<BuilderRun, 'started_at' | 'created_at' | 'finished_at'>, now: number): number {
  const startIso = run.started_at || run.created_at;
  const start = startIso ? Date.parse(startIso) : NaN;
  if (!Number.isFinite(start)) return 0;
  const end = run.finished_at ? Date.parse(run.finished_at) : now;
  return Math.max(0, (Number.isFinite(end) ? end : now) - start);
}

/**
 * 6.12, 18.3: "Retry from where it stopped" applies to a failed run whose
 * second job had started (it carries an estimate), that nobody asked to
 * cancel. The server decides; this only chooses whether to offer the button.
 */
export function canRetryRun(run: Pick<BuilderRun, 'status' | 'estimate' | 'cancel_requested_at' | 'started_at'>): boolean {
  return run.status === 'failed' && Boolean(run.estimate) && Boolean(run.started_at) && !run.cancel_requested_at;
}

// ---------------------------------------------------------------------------
// Steering (6.2, 8.2, 18.2 Q12)
// ---------------------------------------------------------------------------

/** The categories a builder run may take (every category but sop: SOP training is the SOP library's). */
export const BUILDER_CATEGORY_ORDER: CourseCategory[] = ['compliance', 'clinical_skills', 'best_practices', 'platform', 'custom'];

export const BUILDER_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  BUILDER_CATEGORY_ORDER.map((c) => [c, CATEGORY_LABELS[c]])
);

/** The kinds a new built course may take (read and acknowledge courses are the SOP library's). */
export const BUILDER_KINDS: CourseKind[] = ['quiz', 'competency'];

/** 18.2 Q12: a best_practices run builds a scenario. */
export function isScenarioCategory(category: CourseCategory | string | null | undefined): boolean {
  return category === 'best_practices';
}

/** The course lengths offered for a category (18.2 Q12: 3 to 6 minutes for a scenario). */
export function minutesOptions(category: CourseCategory | string | null | undefined): readonly number[] {
  return isScenarioCategory(category) ? BUILDER_SCENARIO_TARGET_MINUTES : BUILDER_TARGET_MINUTES;
}

/** The pool sizes offered for a category (18.2 Q12: 3 to 6 questions for a scenario). */
export function poolOptions(category: CourseCategory | string | null | undefined): readonly number[] {
  return isScenarioCategory(category) ? BUILDER_SCENARIO_POOL_SIZES : BUILDER_POOL_SIZES;
}

/** The default length and pool for a category. */
export function defaultSizes(category: CourseCategory | string | null | undefined): { minutes: number; pool: number } {
  return isScenarioCategory(category) ? { minutes: 4, pool: 4 } : { minutes: 60, pool: 30 };
}

/** 6.4: a standard course plans about one section per 8 minutes, 4 to 12; a scenario plans one. */
export function sectionsImplied(category: CourseCategory | string | null | undefined, minutes: number): number {
  if (isScenarioCategory(category)) return 1;
  return Math.max(4, Math.min(12, Math.round(minutes / 8)));
}

/** The draw a pool implies: half for a course, the whole pool for a scenario (6.2, 18.2 Q12). */
export function drawOf(category: CourseCategory | string | null | undefined, pool: number): number {
  return isScenarioCategory(category) ? pool : Math.floor(pool / 2);
}

/** 7.3: the lint's minimum number of sources for a category (the config route's table when given). */
export function sourceMinimum(category: CourseCategory | string | null | undefined, table?: BuilderConfig['source_minimum'] | null): number {
  if (category === 'compliance') return table?.compliance ?? 3;
  if (category === 'sop') return table?.sop ?? 1;
  return table?.other ?? 2;
}

/** The running count of 8.2 step 1 ("2 of 3 sources for a compliance course"). */
export function sourceCountLine(count: number, minimum: number, category: CourseCategory | string | null | undefined): string {
  const label = category && BUILDER_CATEGORY_LABELS[category] ? BUILDER_CATEGORY_LABELS[category].toLowerCase() : 'this';
  return `${count} of ${minimum} source${minimum === 1 ? '' : 's'} a ${label} course needs (files, web links and citations together)`;
}

// ---------------------------------------------------------------------------
// The wizard's own checks (the server re-checks each one; 6.2, 9.3, 18.2 Q3, Q7)
// ---------------------------------------------------------------------------

const EXTENSIONS: Record<string, string> = { pdf: 'PDF', docx: 'Word (DOCX)', txt: 'text', md: 'Markdown' };

/** Why a chosen file cannot be sent, or null. */
export function builderFileProblem(file: File, maxBytes: number = BUILDER_FILE_MAX_BYTES): string | null {
  const name = (file.name || '').toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  if (ext === 'pptx' || ext === 'ppt') return 'PowerPoint files are not read: export the deck to PDF first.';
  if (!EXTENSIONS[ext]) return 'The file must be a PDF, a Word (DOCX) document, a text file or a Markdown file.';
  if (file.size === 0) return 'The file is empty.';
  if (file.size > maxBytes) return `The file is larger than ${Math.round(maxBytes / (1024 * 1024))} MB.`;
  return null;
}

/** 18.2 Q7: why a web link cannot be sent, or null (the server also resolves the host). */
export function linkProblem(raw: string): string | null {
  const value = (raw || '').trim();
  if (!value) return 'Enter the web address.';
  if (value.length > BUILDER_LINK_MAX_CHARS) return `The address is longer than ${BUILDER_LINK_MAX_CHARS} characters.`;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return 'The address is not a web address.';
  }
  if (url.protocol !== 'https:') return 'Only https addresses are accepted.';
  if (url.username || url.password) return 'The address must not carry a user name or password.';
  const host = url.hostname.toLowerCase();
  if (!host) return 'The address has no host.';
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith('[')) return 'Use the site name, not an IP address.';
  if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local') || host.endsWith('.internal')) {
    return 'The address must be a public site.';
  }
  return null;
}

/** 7.3: a citation without a file needs a title, a publisher and an https address. */
export function citationProblem(entry: { title: string; publisher: string; url: string }): string | null {
  if (!entry.title.trim()) return 'Enter the title.';
  if (!entry.publisher.trim()) return 'Enter the publisher.';
  if (!/^https:\/\/\S+$/i.test(entry.url.trim())) return 'Enter an https address.';
  return null;
}

/**
 * The spend check of 8.2 step 3: the plain-words reason Start is refused
 * before any spend (the server re-checks at Start), or null.
 */
export function capProblem(estimate: BuilderEstimate | null | undefined): string | null {
  if (!estimate) return null;
  const cents = centsOf(estimate.cents);
  const caps = estimate.caps;
  if (!caps) return null;
  if (typeof caps.run === 'number' && cents > caps.run) {
    return `This build is estimated above the per-run cap of ${dollars(caps.run)}. Use fewer or shorter documents, or ask for the cap to be raised.`;
  }
  if (typeof caps.daily_remaining === 'number' && cents > caps.daily_remaining) {
    return `Only ${dollars(caps.daily_remaining)} is left under today's cap for your network. Try again tomorrow (the day is counted in UTC).`;
  }
  if (typeof caps.monthly_remaining === 'number' && cents > caps.monthly_remaining) {
    return `Only ${dollars(caps.monthly_remaining)} is left under this month's cap. Ask for the cap to be raised, or wait for the next month.`;
  }
  return null;
}

/** A file size in words ("820 KB", "12.4 MB"). */
export function fileSizeLabel(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString()} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Cents as dollars, rounded to the cent. */
export function dollars(cents: unknown): string {
  return `$${(Math.round(centsOf(cents)) / 100).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// The policy notice and the attestation (9.2, verbatim)
// ---------------------------------------------------------------------------

export const UPLOAD_POLICY_TEXT =
  "Upload training material only: regulations, guidance, standards, manuals and your site's procedures. Do not upload patient records, study data or anything that names a patient. The text of these files is sent to the platform's AI provider to build the course, stored in the Learning Center's own bucket and kept as the course's sources.";

export const ATTESTATION_LABEL = 'These files contain no patient information';

// ---------------------------------------------------------------------------
// Error copy (Appendix A, 6.12, 18.3, 18.4, 18.5)
// ---------------------------------------------------------------------------

export const ERROR_COPY: Record<string, string> = {
  LMS_BUILDER_DISABLED: 'The course builder is switched off for the Learning Center.',
  LMS_BUILDER_NETWORK_NOT_ALLOWED: 'The course builder is not switched on for your network.',
  LMS_BUILDER_UNSUPPORTED_FILE:
    'A file is not a type the builder reads. Use PDF, Word (DOCX), text or Markdown files; export a PowerPoint deck to PDF first.',
  LMS_BUILDER_SOURCES_INSUFFICIENT:
    'The course needs more sources. Add files, web links or citations until the count meets the minimum for the category.',
  LMS_BUILDER_ATTESTATION_REQUIRED: 'Tick "These files contain no patient information" before the files are sent.',
  LMS_BUILDER_SOURCE_REFUSED:
    'A file could not be read: it may be a scanned document with no text, empty, not UTF-8 text, or too long. Check the file named below and build again without it or with a text version.',
  LMS_BUILDER_LINK_FETCH_FAILED:
    'A web link could not be downloaded. Check the address named below, then create a new run without it or with the corrected link. Nothing was spent.',
  LMS_BUILDER_NO_SOURCES:
    'There is no source text to rebuild this section from. Upload the file the section should be written from.',
  LMS_BUILDER_SECTION_LIMIT: 'This draft has used all of its builder passes. Edit the section by hand, or start a new build.',
  LMS_BUILDER_QUEUE_UNAVAILABLE:
    'The builder could not reach its job queue. Nothing was spent. Try again in a few minutes.',
  LMS_BUILDER_UPLOAD_FAILED: 'The files could not be stored. Nothing was spent. Try the upload again.',
  LMS_BUILDER_RATE_LIMITED: 'Too many builds were started in the last few minutes. Wait ten minutes and try again.',
  LMS_BUILDER_RATE_LIMIT_UNAVAILABLE:
    'The builder could not check its rate limit, so it did not start (it never runs unmetered). Try again in a few minutes.',
  LMS_BUILDER_BUDGET_CHECK_UNAVAILABLE:
    'The builder could not check its spending caps, so it did not start (it never runs unmetered). Try again in a few minutes.',
  LMS_BUILDER_RUN_CAP_EXCEEDED:
    'This build reached the per-run spending cap and stopped. What was written so far is kept; a raised cap and Retry continue from where it stopped.',
  LMS_BUILDER_DAILY_BUDGET_EXCEEDED:
    "Today's spending cap for your network is reached. Retry tomorrow (the day is counted in UTC); nothing written so far is lost.",
  LMS_BUILDER_MONTHLY_BUDGET_EXCEEDED:
    "This month's spending cap is reached. Ask for the cap to be raised, or retry next month; nothing written so far is lost.",
  LMS_BUILDER_MODEL_OUTPUT_INVALID:
    'The AI answered in a shape the builder could not use, twice. Retry, or rewrite the instruction; the stage and section are named below.',
  LMS_BUILDER_MODEL_OUTPUT_TRUNCATED:
    'The AI ran out of room before it finished writing (its answer was cut off at the longest answer it can give), so the build stopped instead of asking again. Build a smaller course, split the material, or ask the platform owner to check the output limit; the stage, the section and the length reached are named below.',
  LMS_BUILDER_MODEL_REFUSED:
    'The AI declined to write from this material. Try a different document, or rewrite the instruction.',
  LMS_BUILDER_MODEL_TIMEOUT: 'The AI took too long to answer. Retry from where it stopped.',
  LMS_BUILDER_MODEL_ERROR: 'The AI service answered with an error. Retry from where it stopped.',
  LMS_BUILDER_MODEL_ACCESS_DENIED:
    'The platform does not have access to the AI model the builder is set to use. The platform owner must enable it.',
  LMS_BUILDER_MODEL_UNSUPPORTED:
    'The AI model refused the request format. The platform owner must check the builder model setting.',
  LMS_BUILDER_BAD_REQUEST: 'The AI service refused the request. Retry; if it happens again, tell the platform owner.',
  LMS_BUILDER_ASSEMBLY_INPUT_INVALID:
    'The written sections could not be put together into a course. Retry from where it stopped.',
  LMS_BUILDER_MERGE_PLAN_INVALID:
    'The plan for adding the new material to the course kept or changed the wrong sections, twice. Retry, or change which sections are revised.',
  LMS_BUILDER_ARTIFACT_MISSING: 'A stored step of this build could not be found. Start a new build.',
  LMS_BUILDER_JOB_FAILED: 'The build stopped on an unexpected error. Retry from where it stopped.',
  LMS_BUILDER_JOB_INVALID: 'The build job was not valid. Start a new build.',
  LMS_BUILDER_PIPELINE_UNAVAILABLE: 'The builder is not running on the server. Try again later.',
  LMS_REAUTH_FAILED: 'The password was not accepted.',
  LMS_CONFLICT:
    'Something changed while you were working: an open draft or another build on this course, a run in the wrong state, or a course that already uses the same short name. Reload and check the course.',
  LMS_IMMUTABLE: 'That version is published and cannot be changed. Make a new draft first.',
  LMS_ADMIN_REQUIRED: 'Only Learning Center admins can use the course builder.',
  LMS_NOT_FOUND: 'The run, course or file was not found.',
  LMS_INVALID: 'Something in the request was not accepted; the reason is below.',
};

/** Plain words for a code, or `fallback` when the code has none. */
export function errorCopy(code: string | null | undefined, fallback = 'The course builder could not complete the request.'): string {
  if (code && Object.prototype.hasOwnProperty.call(ERROR_COPY, code)) return ERROR_COPY[code];
  return fallback;
}

/**
 * What the admin reads for a refused request: the code's plain words, then
 * the server's own message when it adds something (a file name, a minimum).
 */
export function explainError(code: string | null | undefined, message: string | null | undefined, fallback?: string): string {
  const copy = errorCopy(code, fallback ?? (message || undefined));
  const detail = (message || '').trim();
  if (!detail || detail === copy || !code || !Object.prototype.hasOwnProperty.call(ERROR_COPY, code)) return copy;
  return `${copy} (${detail})`;
}
